import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";

const ACCOUNTANT_PREFIX = "accountantProfessionalRequests";
const NOTARY_PREFIX = "notary";

/**
 * Certificate request list/detail copy: notaries use `notary.*`; accountants use
 * `accountantProfessionalRequests.*` when a key exists, otherwise the same as notaries.
 */
export function useProfessionalRequestCopy(): {
  isAccountant: boolean;
  /** e.g. `requestDetail.titleWithType` or `requests.subtitle` */
  tp: (notaryRelativePath: string) => string;
  tpo: (notaryRelativePath: string, options?: Record<string, unknown>) => string;
  /**
   * Per-request-type copy: tries `<path>_<requestType>` and falls back to
   * `<path>`, each through the same accountant→notary cascade as `tp`.
   *
   * WHAT THIS IS *NOT* FOR ANY MORE: swapping the deliverable noun. Every
   * surface now calls the work "el trabajo" and the request "la gestión",
   * whatever the request type, so the eleven `_custom_quote` variants this
   * used to resolve were folded into their base keys and deleted.
   *
   * What survives is the one thing a request type genuinely does change: the
   * NAME OF THE PRODUCT. `pricingLineService_notarial_certificate` reads
   * "Certificado digital de empresa" because that is what the consumer
   * ordered — a proper name, not the generic noun for the deliverable.
   * Exactly one call site is left, and new variants should only ever be
   * product names.
   *
   * A missing variant falls back rather than throwing, so a typo'd key shows
   * the base wording instead of crashing the page.
   */
  tpt: (notaryRelativePath: string, requestType?: string | null) => string;
} {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAccountant =
    user?.role === "accountant" || user?.isAccountant === true;

  const tp = useCallback(
    (notaryRelativePath: string) => {
      const accountantKey = `${ACCOUNTANT_PREFIX}.${notaryRelativePath}`;
      if (isAccountant && i18n.exists(accountantKey)) {
        return t(accountantKey);
      }
      return t(`${NOTARY_PREFIX}.${notaryRelativePath}`);
    },
    [isAccountant, i18n, t],
  );

  const tpo = useCallback(
    (notaryRelativePath: string, options?: Record<string, unknown>) => {
      const accountantKey = `${ACCOUNTANT_PREFIX}.${notaryRelativePath}`;
      if (isAccountant && i18n.exists(accountantKey)) {
        return t(accountantKey, options);
      }
      return t(`${NOTARY_PREFIX}.${notaryRelativePath}`, options);
    },
    [isAccountant, i18n, t],
  );

  const tpt = useCallback(
    (notaryRelativePath: string, requestType?: string | null) => {
      const typeKey = requestType?.trim();
      if (typeKey) {
        const variantPath = `${notaryRelativePath}_${typeKey}`;
        const accountantVariant = `${ACCOUNTANT_PREFIX}.${variantPath}`;
        if (isAccountant && i18n.exists(accountantVariant)) {
          return t(accountantVariant);
        }
        const notaryVariant = `${NOTARY_PREFIX}.${variantPath}`;
        if (i18n.exists(notaryVariant)) {
          return t(notaryVariant);
        }
      }
      return tp(notaryRelativePath);
    },
    [isAccountant, i18n, t, tp],
  );

  return { isAccountant, tp, tpo, tpt };
}
